<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import type {
  MailSendLimitRequestRow,
  MailSendLimitDecisionResult
} from '@/types/dashboard/mail'

const { t, d } = useI18n()
const notifyStore = useNotifyStore()

type Action = 'approve' | 'deny' | 'restrict' | 'unrestrict' | 'withdraw'

/** Only these two carry a note; the API requires one and rejects without it. */
const NOTE_ACTIONS: Action[] = ['deny', 'restrict']

const rows = ref<MailSendLimitRequestRow[]>([])
const loading = ref(false)
const busy = ref<number | null>(null)
const statusFilter = ref('pending')

const dialogOpen = ref(false)
const pendingAction = ref<{ action: Action; row: MailSendLimitRequestRow } | null>(null)

const filterItems = computed(() =>
  ['pending', 'throttled', 'approved', 'denied', 'all'].map((value) => ({
    value,
    title: t(`dashboard.mail.admin.sendLimits.filter.${value}`)
  }))
)

async function load() {
  loading.value = true
  try {
    const res = await useAxios<never, MailSendLimitRequestRow[]>(
      'GET',
      `/admin/mail/send-limit/requests?status=${statusFilter.value}`,
      true
    )
    rows.value = res.data
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AdminMailSendLimits' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.mail.admin.sendLimits.loadError'),
      message: getErrorMessage(error)
    })
  } finally {
    loading.value = false
  }
}

function start(action: Action, row: MailSendLimitRequestRow) {
  if (NOTE_ACTIONS.includes(action)) {
    pendingAction.value = { action, row }
    dialogOpen.value = true
    return
  }
  void run(action, row)
}

async function run(action: Action, row: MailSendLimitRequestRow, note?: string) {
  busy.value = row.account_id
  try {
    const res = await useAxios<{ note?: string }, MailSendLimitDecisionResult>(
      'POST',
      `/admin/mail/send-limit/requests/${row.account_id}/${action}`,
      true,
      note ? { note } : undefined
    )

    dialogOpen.value = false
    pendingAction.value = null

    // guard_sync is not decoration. "failed" means the database is correct but
    // the mail server was not updated, and affected_senders of 0 on a throttle
    // means it reached no mailboxes at all. Both need to reach the operator
    // now, not in a post-mortem.
    const { guard_sync: sync, affected_senders: affected } = res.data
    if (sync === 'failed') {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('dashboard.mail.admin.sendLimits.syncFailed'),
        message: t('dashboard.mail.admin.sendLimits.syncFailedHint')
      })
    } else if (action === 'restrict' && affected === 0) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'warning',
        title: t('dashboard.mail.admin.sendLimits.noSendersAffected'),
        message: t('dashboard.mail.admin.sendLimits.noSendersAffectedHint')
      })
    } else {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t(`dashboard.mail.admin.sendLimits.actions.${action}Success`),
        message: t('dashboard.mail.admin.sendLimits.affected', { count: affected })
      })
    }

    await load()
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'AdminMailSendLimits' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.mail.admin.sendLimits.actionError'),
      message: getErrorMessage(error)
    })
  } finally {
    busy.value = null
  }
}

function confirmNote(note: string) {
  const pending = pendingAction.value
  if (pending) void run(pending.action, pending.row, note)
}

/**
 * Approve is unavailable while a throttle is in force, because approving would
 * set the lift flag and send a "limit lifted" mail while the throttle kept the
 * customer restricted. Say so rather than showing a button that 409s.
 */
function canApprove(row: MailSendLimitRequestRow): boolean {
  return row.request_status === 'pending' && !row.forced_restricted
}

function formatDate(value?: string | null): string {
  return value ? d(new Date(value), 'short') : '—'
}

onMounted(load)
</script>

<template>
  <BaseDashboardPage
    icon="mdi-speedometer-slow"
    :title="t('dashboard.mail.admin.sendLimits.title')"
    :subtitle="t('dashboard.mail.admin.sendLimits.subtitle')"
  >
    <div class="filter-bar">
      <BaseSelect
        v-model="statusFilter"
        :items="filterItems"
        item-title="title"
        item-value="value"
        :label="t('dashboard.mail.admin.sendLimits.filter.label')"
        @update:model-value="load"
      />
    </div>

    <p v-if="loading" class="state-text">{{ t('common.loading') }}</p>
    <p v-else-if="rows.length === 0" class="state-text">
      {{ t('dashboard.mail.admin.sendLimits.empty') }}
    </p>

    <div v-else class="request-list">
      <article v-for="row in rows" :key="row.account_id" class="request-card">
        <header class="request-header">
          <div>
            <h3>{{ row.name || row.email }}</h3>
            <p class="request-email">{{ row.email }} · #{{ row.account_id }}</p>
          </div>
          <span v-if="row.forced_restricted" class="badge badge-throttled">
            {{ t('dashboard.mail.admin.sendLimits.throttled') }}
          </span>
          <span v-else class="badge">{{
            t(`dashboard.mail.sendLimit.statusLabel.${row.request_status}`)
          }}</span>
        </header>

        <!-- Decision signals. None of these automates the call, which is why
             approval is manual, but they are what a human needs to make it. -->
        <dl class="signals">
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.age') }}</dt>
            <dd>{{ t('dashboard.mail.admin.sendLimits.days', { n: row.age_days }) }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.mailboxes') }}</dt>
            <dd>{{ row.mailbox_count }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.outbound') }}</dt>
            <dd>{{ row.outbound_7d }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.verified') }}</dt>
            <dd>{{ row.email_verified ? '✓' : '—' }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.payment') }}</dt>
            <dd>{{ row.has_payment_method ? '✓' : '—' }}</dd>
          </div>
          <div>
            <dt>{{ t('dashboard.mail.admin.sendLimits.signals.requested') }}</dt>
            <dd>{{ formatDate(row.requested_at) }}</dd>
          </div>
        </dl>

        <!-- Customer-written text, rendered as text. Never v-html: it is
             untrusted, and it is read under time pressure. -->
        <blockquote v-if="row.reason" class="reason">{{ row.reason }}</blockquote>
        <p v-if="row.forced_reason" class="forced-reason">
          {{ t('dashboard.mail.admin.sendLimits.forcedReason', { reason: row.forced_reason }) }}
        </p>

        <p v-if="row.request_status === 'pending' && row.forced_restricted" class="approve-blocked">
          {{ t('dashboard.mail.admin.sendLimits.approveBlocked') }}
        </p>

        <div class="actions">
          <button
            v-if="row.request_status === 'pending'"
            class="act act-approve"
            :disabled="busy === row.account_id || !canApprove(row)"
            @click="start('approve', row)"
          >
            {{ t('dashboard.mail.admin.sendLimits.actions.approve') }}
          </button>
          <button
            v-if="row.request_status === 'pending'"
            class="act"
            :disabled="busy === row.account_id"
            @click="start('deny', row)"
          >
            {{ t('dashboard.mail.admin.sendLimits.actions.deny') }}
          </button>
          <button
            v-if="!row.forced_restricted"
            class="act act-danger"
            :disabled="busy === row.account_id"
            @click="start('restrict', row)"
          >
            {{ t('dashboard.mail.admin.sendLimits.actions.restrict') }}
          </button>
          <button
            v-else
            class="act"
            :disabled="busy === row.account_id"
            @click="start('unrestrict', row)"
          >
            {{ t('dashboard.mail.admin.sendLimits.actions.unrestrict') }}
          </button>
          <button
            v-if="row.request_status === 'approved'"
            class="act act-danger"
            :disabled="busy === row.account_id"
            @click="start('withdraw', row)"
          >
            {{ t('dashboard.mail.admin.sendLimits.actions.withdraw') }}
          </button>
        </div>
      </article>
    </div>

    <DashboardReasonConfirmDialog
      v-model="dialogOpen"
      :variant="pendingAction?.action === 'restrict' ? 'danger' : 'warning'"
      :title="
        pendingAction
          ? t(`dashboard.mail.admin.sendLimits.actions.${pendingAction.action}Title`)
          : ''
      "
      :description="
        pendingAction
          ? t('dashboard.mail.admin.sendLimits.confirmFor', { email: pendingAction.row.email })
          : ''
      "
      :reason-label="t('dashboard.mail.admin.sendLimits.noteLabel')"
      :reason-placeholder="t('dashboard.mail.admin.sendLimits.notePlaceholder')"
      :min-length="10"
      :max-length="1000"
      @confirm="confirmNote"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.filter-bar {
  max-width: 260px;
  margin-bottom: 20px;
}

.state-text {
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.9rem;
}

.request-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.request-card {
  background: rgba(15, 23, 48, 0.55);
  border: 1px solid rgba(112, 164, 255, 0.14);
  border-radius: 14px;
  padding: 18px 20px;
}

.request-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.92);
  }
}

.request-email {
  margin: 2px 0 0 0;
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.55);
}

.badge {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  background: rgba(112, 164, 255, 0.14);
  color: rgba(200, 218, 255, 0.85);

  &.badge-throttled {
    background: rgba(244, 67, 54, 0.16);
    color: #ff8a80;
  }
}

.signals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px 16px;
  margin: 0 0 14px 0;

  dt {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(200, 218, 255, 0.45);
  }

  dd {
    margin: 2px 0 0 0;
    font-size: 0.88rem;
    color: rgba(255, 255, 255, 0.88);
  }
}

.reason {
  margin: 0 0 12px 0;
  padding: 10px 14px;
  border-left: 3px solid rgba(112, 164, 255, 0.4);
  border-radius: 0 8px 8px 0;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.85rem;
  line-height: 1.55;
  color: rgba(200, 218, 255, 0.85);
  white-space: pre-wrap;
}

.forced-reason,
.approve-blocked {
  margin: 0 0 12px 0;
  font-size: 0.78rem;
  color: rgba(255, 138, 128, 0.9);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.act {
  padding: 7px 14px;
  border: 1px solid rgba(112, 164, 255, 0.22);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &.act-approve { background: rgba(29, 233, 182, 0.16); border-color: rgba(29, 233, 182, 0.3); }
  &.act-danger { background: rgba(244, 67, 54, 0.14); border-color: rgba(244, 67, 54, 0.28); }
}
</style>

<style lang="scss">
/* Light theme, unscoped per the repo's Vuetify styling rule. */
.v-theme--lightTheme {
  .request-card {
    background: rgba(15, 23, 42, 0.03);
    border-color: rgba(15, 23, 42, 0.1);
  }
  .request-header h3 { color: rgba(15, 23, 42, 0.9); }
  .request-email,
  .state-text,
  .signals dt { color: rgba(15, 23, 42, 0.55); }
  .signals dd { color: rgba(15, 23, 42, 0.85); }
  .reason {
    background: rgba(15, 23, 42, 0.04);
    color: rgba(15, 23, 42, 0.8);
  }
  .act { color: rgba(15, 23, 42, 0.85); background: rgba(15, 23, 42, 0.04); }
}
</style>
